import { useState, useEffect, useRef } from 'react';
import { Link, useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { CreateDraftModal } from '../drafts/CreateDraftModal';
import { NotesList } from '../notes/NotesList';
import { SpeakerList } from './SpeakerList';
import { SessionList } from './SessionList';
import { DraftList } from './DraftList';
import { AddressInput, emptyAddress } from '../../components/AddressInput';
import { AddressDisplay } from '../../components/AddressDisplay';
import { parseAddress, stringifyAddress, isAddressEmpty } from '../../lib/formatters';
import {
  EVENT_CONFIRMED,
  EVENT_COMPLETED,
  EVENT_CANCELLED,
  EVENT_ARCHIVED,
} from '../../lib/constants';

// Fallback used when the Worker is unreachable in dev. Same shape as the API.
const MOCK_EVENT = {
  id: 'a1b2c3d4e5f60001',
  name: 'Heritage Summit Kraków',
  status: EVENT_CONFIRMED,
  eventDate: '2026-06-12',
  eventTime: '09:00–17:30',
  location: 'ICE Kraków',
  // Stored as a JSON string in D1 — kept structured so AddressDisplay renders
  // the multi-line layout in dev when the worker is unreachable.
  address: JSON.stringify({
    line1: 'ul. Marii Konopnickiej 17',
    line2: '',
    city: 'Kraków',
    province: 'Małopolska',
    postCode: '30-302',
    country: 'Poland',
  }),
  organiser: 'European Heritage Hub',
  website: 'https://heritagesummit2026.eu',
  notes: '',
  topics: ['Heritage', 'Digital Preservation', 'Policy'],
  partners: ['UNESCO', 'Europa Nostra', 'MKiDN'],
  customHashtags: ['#HeritageSummit2026', '#HeritageFutures', '#ICEKraków'],
  speakers: [
    { id: 's1', name: 'Anna Nowak',         role: 'Director',             organisation: 'Europa Nostra' },
    { id: 's2', name: 'Dr. Piotr Kowalski', role: 'Chief Curator',        organisation: 'National Museum of Kraków' },
    { id: 's3', name: 'Maria Wiśniewska',   role: 'Heritage Policy Lead', organisation: 'European Commission' },
    { id: 's4', name: 'Janusz Zieliński',   role: 'Founder',              organisation: 'Digital Heritage Foundation' },
  ],
  sessions: [
    { id: 'ss1', time: '09:00–10:30', title: 'Opening Keynote: The Future of European Heritage', speaker: 'Anna Nowak',         type: 'keynote' },
    { id: 'ss2', time: '11:00–12:30', title: 'Panel: Digital Preservation in Practice',          speaker: 'Dr. Piotr Kowalski', type: 'panel' },
    { id: 'ss3', time: '13:30–15:00', title: 'Workshop: Building Heritage Communities Online',   speaker: 'Maria Wiśniewska',   type: 'workshop' },
    { id: 'ss4', time: '15:30–16:30', title: 'Roundtable: Policy & Funding for Cultural Heritage', speaker: 'Janusz Zieliński', type: 'roundtable' },
    { id: 'ss5', time: '16:45–17:30', title: 'Closing Remarks & Networking',                     speaker: '',                   type: 'networking' },
  ],
};

const TABS = [
  { key: 'details',     labelKey: 'events.detail.tabs.details' },
  { key: 'preEvent',    labelKey: 'events.detail.tabs.preEvent' },
  { key: 'duringEvent', labelKey: 'events.detail.tabs.duringEvent' },
  { key: 'postEvent',   labelKey: 'events.detail.tabs.postEvent' },
];

const ACTION_BUTTONS = [
  'events.detail.actions.briefing',
  'events.detail.actions.stories',
  'events.detail.actions.teamPlan',
  'events.detail.actions.addInfo',
];
// Note: Create-Draft is rendered separately as a primary button so it stands out.

const STATUS_OPTIONS = [
  { status: EVENT_CONFIRMED, labelKey: 'events.detail.statusActions.markConfirmed' },
  { status: EVENT_COMPLETED, labelKey: 'events.detail.statusActions.markCompleted' },
  { status: EVENT_CANCELLED, labelKey: 'events.detail.statusActions.cancelEvent' },
  { status: EVENT_ARCHIVED,  labelKey: 'events.detail.statusActions.archiveEvent' },
];

function formatDate(eventDate, language) {
  if (!eventDate) return '';
  const locale = language === 'pl' ? 'pl-PL' : 'en-GB';
  return new Date(`${eventDate}T00:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// Quick deep-equality check for the small subset of values we compare:
// strings, nulls, and arrays of strings.
function valuesEqual(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  if ((a == null || a === '') && (b == null || b === '')) return true;
  return a === b;
}

// ─────────────────────────────────────────────────────────────────────────────
// Status dropdown
// ─────────────────────────────────────────────────────────────────────────────
function StatusDropdown({ status, onChange, onDelete }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="status-dropdown" ref={ref}>
      <button
        type="button"
        className={`status-dropdown__trigger status-badge status-badge--${status}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {t(`events.statuses.${status}`)} ▾
      </button>
      {open && (
        <div className="status-dropdown__menu" role="menu">
          {STATUS_OPTIONS.filter((option) => option.status !== status).map((option) => (
            <button
              key={option.status}
              type="button"
              role="menuitem"
              className="status-dropdown__option"
              onClick={() => { onChange(option.status); setOpen(false); }}
            >
              {t(option.labelKey)}
            </button>
          ))}
          <div className="status-dropdown__divider" />
          <button
            type="button"
            role="menuitem"
            className="status-dropdown__option status-dropdown__option--danger"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            {t('events.detail.statusActions.deletePermanently')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Editable field — onChange is local-only, onCommit fires on blur (PATCH)
// ─────────────────────────────────────────────────────────────────────────────
function EditableField({ labelKey, value, onChange, onCommit, type = 'text', placeholder, multiline, disabled }) {
  const { t } = useTranslation();
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <label className="editable-field">
      <span className="editable-field__label">{t(labelKey)}</span>
      <Tag
        className={
          'editable-field__input' + (multiline ? ' editable-field__input--multiline' : '')
        }
        type={multiline ? undefined : type}
        value={value || ''}
        placeholder={placeholder}
        rows={multiline ? 4 : undefined}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onBlur={(event) => onCommit?.(event.target.value)}
      />
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Chip section (topics / partners / custom hashtags)
// ─────────────────────────────────────────────────────────────────────────────
function ChipSection({ titleKey, subtitleKey, chips, onAdd, onRemove, placeholder, variant }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  function commit() {
    const value = draft.trim();
    if (!value) return;
    onAdd(value);
    setDraft('');
  }

  return (
    <section className="chip-section">
      <header className="chip-section__header">
        <h3 className="chip-section__title">{t(titleKey)}</h3>
        {subtitleKey && (
          <div className="chip-section__subtitle">{t(subtitleKey)}</div>
        )}
      </header>

      {chips.length > 0 && (
        <div className="chip-section__chips">
          {chips.map((chip) => (
            <span key={chip} className={'chip' + (variant ? ` chip--${variant}` : '')}>
              <span>{chip}</span>
              <button
                type="button"
                className="chip__remove"
                onClick={() => onRemove(chip)}
                aria-label={`${t('common.remove')} ${chip}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="chip-input">
        <input
          className="chip-input__field"
          type="text"
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
        />
        <button type="button" className="chip-input__add" onClick={commit} aria-label={t('common.add')}>
          +
        </button>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AddressField — read state shows AddressDisplay; click → AddressInput.
// Save commits the whole edit at once via onCommit (one PATCH per session,
// not per keystroke) and onDirtyChange lets the parent's beforeunload guard
// know the field has unsaved edits.
// ─────────────────────────────────────────────────────────────────────────────
function AddressField({ value, onCommit, onDirtyChange, disabled }) {
  const { t } = useTranslation();
  const parsedValue = parseAddress(value);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(parsedValue);
  const [saving, setSaving] = useState(false);

  function startEdit() {
    if (disabled) return;
    setDraft(parseAddress(value));
    setEditing(true);
    onDirtyChange?.(true);
  }

  function cancelEdit() {
    setDraft(parseAddress(value));
    setEditing(false);
    onDirtyChange?.(false);
  }

  async function saveEdit() {
    setSaving(true);
    try {
      await onCommit(stringifyAddress(draft));
      setEditing(false);
      onDirtyChange?.(false);
    } finally {
      setSaving(false);
    }
  }

  const empty = isAddressEmpty(parsedValue);

  return (
    <div className="address-field">
      <div className="address-field__header">
        <span className="editable-field__label">{t('events.detail.fields.address')}</span>
        {!editing && !disabled && (
          <button
            type="button"
            className="address-field__edit"
            onClick={startEdit}
          >
            {empty ? t('address.add') : t('common.edit')}
          </button>
        )}
      </div>

      {editing ? (
        <>
          <AddressInput
            value={draft}
            onChange={setDraft}
            disabled={saving}
          />
          <div className="address-field__actions">
            <button
              type="button"
              className="button button--ghost"
              onClick={cancelEdit}
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="button button--primary"
              onClick={saveEdit}
              disabled={saving}
            >
              {saving ? t('common.saveSaving') : t('common.save')}
            </button>
          </div>
        </>
      ) : (
        <AddressDisplay
          value={parsedValue}
          emptyFallback={
            <span className="address-field__empty">{t('address.empty')}</span>
          }
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Confirm-delete dialog
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmDeleteDialog({ eventName, onCancel, onConfirm, deleting }) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const matches = typed.trim() === eventName;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal__title">{t('events.detail.delete.title')}</h2>
        <p className="modal__body">{t('events.detail.delete.body', { name: eventName })}</p>

        <label className="editable-field">
          <span className="editable-field__label">{t('events.detail.delete.confirmLabel')}</span>
          <input
            className="modal__input"
            type="text"
            value={typed}
            autoFocus
            onChange={(event) => setTyped(event.target.value)}
          />
        </label>

        <div className="modal__actions">
          <button type="button" className="button button--ghost" onClick={onCancel} disabled={deleting}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="button button--danger"
            disabled={!matches || deleting}
            onClick={onConfirm}
          >
            {t('events.detail.delete.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Coming-soon panel for non-Details tabs
// ─────────────────────────────────────────────────────────────────────────────
function ComingSoonPanel({ phase, tabLabel }) {
  const { t } = useTranslation();
  return (
    <div className="coming-soon">
      <div className="coming-soon__icon" aria-hidden="true">🚧</div>
      <h2>{t('events.detail.comingSoon.title', { phase })}</h2>
      <p>{t('events.detail.comingSoon.body', { tab: tabLabel.toLowerCase() })}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Save toast (fixed top-right, fires on every PATCH)
// ─────────────────────────────────────────────────────────────────────────────
function SaveToast({ status }) {
  const { t } = useTranslation();
  if (status === 'idle') return null;
  const label =
    status === 'saving' ? t('events.detail.saveSaving') :
    status === 'saved'  ? t('events.detail.saveSaved')  :
                          t('events.detail.saveError');
  return (
    <div className="toast-region" aria-live="polite">
      <div className={`toast toast--${status}`} role="status">
        {label}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export function EventDetail() {
  const { t, language } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();

  // `event` is the live, optimistically-updated state (what the user sees).
  // `savedEvent` is the snapshot of what's on the server — used to decide
  // whether a blur represents a real change or a no-op blur.
  const [event, setEvent] = useState(null);
  const [savedEvent, setSavedEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [usingMockData, setUsingMockData] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [activeTab, setActiveTab] = useState('details');
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCreateDraft, setShowCreateDraft] = useState(false);

  // Dirty tracking. `dirtyFields` covers "user typed but blur/save hasn't fired
  // yet"; `pendingSaves` covers "PATCH is in flight". Either makes the page dirty.
  const [dirtyFields, setDirtyFields] = useState(() => new Set());
  const [pendingSaves, setPendingSaves] = useState(0);
  const isDirty = dirtyFields.size > 0 || pendingSaves > 0;

  const savedTimerRef = useRef(null);

  // ── Browser-level: warn before tab close / refresh / external navigation.
  useEffect(() => {
    if (!isDirty) return undefined;
    function handleBeforeUnload(e) {
      e.preventDefault();
      // Modern browsers ignore the message and show their own; setting
      // returnValue is what actually triggers the dialog.
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // ── In-app: block React Router navigation when dirty.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const ok = window.confirm(t('events.detail.unsavedConfirm'));
      if (ok) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker, t]);

  // Load event on mount or when eventId changes.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);
    setUsingMockData(false);

    api.events.get(eventId)
      .then((data) => {
        if (cancelled) return;
        setEvent(data);
        setSavedEvent(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 404) {
          setNotFound(true);
        } else {
          // Network/other error — fall back to MOCK_EVENT so the screen still works in dev.
          console.warn('[event] API unreachable, using mock data:', err.message);
          setEvent(MOCK_EVENT);
          setSavedEvent(MOCK_EVENT);
          setUsingMockData(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [eventId]);

  // Cleanup the "Saved ✓" auto-clear timer on unmount.
  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  function flashSaved() {
    setSaveStatus('saved');
    clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => {
      setSaveStatus((s) => (s === 'saved' ? 'idle' : s));
    }, 2000);
  }

  function flashError() {
    setSaveStatus('error');
    clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => {
      setSaveStatus((s) => (s === 'error' ? 'idle' : s));
    }, 4000);
  }

  // Local-only update — fires on every keystroke. Marks the field dirty so the
  // before-unload + blocker know to warn even before blur fires.
  function updateLocal(field, value) {
    setEvent((prev) => ({ ...prev, [field]: value }));
    setDirtyFields((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  }

  // Optimistic update + immediate PATCH (no debounce). Rolls back on failure.
  // Compares the new value against the *server* snapshot (savedEvent), NOT the
  // live `event` — because typing has already updated `event[field]` via
  // updateLocal, so `event[field] === value` always at blur time.
  async function patchField(field, value) {
    if (!event || !savedEvent || !eventId) return;
    const saved = savedEvent[field];
    console.log('[event] patchField:', field, '→', value, '(saved was:', saved, ')');

    if (valuesEqual(saved, value)) {
      // Blur after typing-then-reverting. No real change vs server — clear dirty.
      setDirtyFields((prev) => {
        if (!prev.has(field)) return prev;
        const next = new Set(prev);
        next.delete(field);
        return next;
      });
      return;
    }

    setEvent((prev) => ({ ...prev, [field]: value }));
    setSaveStatus('saving');
    setPendingSaves((p) => p + 1);

    try {
      await api.events.update(eventId, { [field]: value });
      setSavedEvent((prev) => ({ ...prev, [field]: value }));
      setDirtyFields((prev) => {
        if (!prev.has(field)) return prev;
        const next = new Set(prev);
        next.delete(field);
        return next;
      });
      flashSaved();
    } catch (err) {
      console.error('[event] save failed:', err);
      setEvent((prev) => ({ ...prev, [field]: saved }));
      flashError();
    } finally {
      setPendingSaves((p) => Math.max(0, p - 1));
    }
  }

  function addChip(field, value) {
    let cleaned = value.trim();
    if (!cleaned) return;
    if (field === 'customHashtags') {
      cleaned = cleaned.replace(/\s+/g, '');
      if (!cleaned.startsWith('#')) cleaned = '#' + cleaned;
    }
    if (event[field].includes(cleaned)) return;
    patchField(field, [...event[field], cleaned]);
  }

  function removeChip(field, value) {
    patchField(field, event[field].filter((c) => c !== value));
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.events.delete(eventId);
      navigate('/');
    } catch (err) {
      console.error('[event] delete failed:', err);
      flashError();
      setDeleting(false);
      setShowDelete(false);
    }
  }

  // ── Loading / not-found states ────────────────────────────────────────────
  if (isLoading) {
    return (
      <div>
        <Link to="/" className="event-detail__back">{t('events.detail.backToEvents')}</Link>
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">⏳</div>
          <p>{t('events.detail.loading')}</p>
        </div>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div>
        <Link to="/" className="event-detail__back">{t('events.detail.backToEvents')}</Link>
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">📭</div>
          <h2>{t('events.detail.notFoundTitle')}</h2>
          <p>{t('events.detail.notFoundBody')}</p>
        </div>
      </div>
    );
  }

  const activeTabLabel = t(TABS.find((tab) => tab.key === activeTab).labelKey);
  const empty = t('events.detail.fieldEmpty');

  return (
    <div>
      <Link to="/" className="event-detail__back">{t('events.detail.backToEvents')}</Link>

      {usingMockData && <div className="dev-notice">{t('events.loadError')}</div>}

      <div className="event-detail__header-block">
        <header className="event-detail__header">
          <div className="event-detail__heading">
            <h1>{event.name || empty}</h1>
          </div>
          <div className="event-detail__meta">
            <span><span aria-hidden="true">📅</span> {event.eventDate ? formatDate(event.eventDate, language) : empty}</span>
            <span><span aria-hidden="true">📍</span> {event.location || empty}</span>
            <StatusDropdown
              status={event.status}
              onChange={(s) => patchField('status', s)}
              onDelete={() => setShowDelete(true)}
            />
          </div>
        </header>

        <div className="event-detail__actions">
          <button
            type="button"
            className="button button--primary"
            onClick={() => setShowCreateDraft(true)}
          >
            {t('events.detail.actions.createDraft')}
          </button>
          {ACTION_BUTTONS.map((labelKey) => (
            <button key={labelKey} type="button" className="button button--ghost">
              {t(labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={'tab' + (activeTab === tab.key ? ' tab--active' : '')}
            onClick={() => setActiveTab(tab.key)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {activeTab === 'details' ? (
        <div className="details-grid">
          <div className="details-col">
            <EditableField
              labelKey="events.detail.fields.name"
              value={event.name}
              onChange={(v) => updateLocal('name', v)}
              onCommit={(v) => patchField('name', v.trim())}
            />
            <div className="details-row">
              <EditableField
                labelKey="events.detail.fields.date"
                type="date"
                value={event.eventDate}
                onChange={(v) => updateLocal('eventDate', v)}
                onCommit={(v) => patchField('eventDate', v || null)}
              />
              <EditableField
                labelKey="events.detail.fields.time"
                value={event.eventTime}
                onChange={(v) => updateLocal('eventTime', v)}
                onCommit={(v) => patchField('eventTime', v || null)}
              />
            </div>
            <EditableField
              labelKey="events.detail.fields.location"
              value={event.location}
              onChange={(v) => updateLocal('location', v)}
              onCommit={(v) => patchField('location', v || null)}
            />
            <AddressField
              value={event.address}
              disabled={usingMockData}
              onCommit={(serialised) => patchField('address', serialised)}
              onDirtyChange={(dirty) => {
                setDirtyFields((prev) => {
                  const has = prev.has('address');
                  if (dirty === has) return prev;
                  const next = new Set(prev);
                  if (dirty) next.add('address');
                  else next.delete('address');
                  return next;
                });
              }}
            />
            <EditableField
              labelKey="events.detail.fields.organiser"
              value={event.organiser}
              onChange={(v) => updateLocal('organiser', v)}
              onCommit={(v) => patchField('organiser', v || null)}
            />
            <EditableField
              labelKey="events.detail.fields.website"
              type="url"
              value={event.website}
              onChange={(v) => updateLocal('website', v)}
              onCommit={(v) => patchField('website', v || null)}
            />
            <EditableField
              labelKey="events.detail.fields.notes"
              value={event.notes}
              multiline
              placeholder={t('events.detail.placeholders.notes')}
              onChange={(v) => updateLocal('notes', v)}
              onCommit={(v) => patchField('notes', v || null)}
            />

            {/* Multi-entry context notes — separate from the single `notes`
                field above. These feed into AI generation per FEATURES-ADDITIONS §2.10. */}
            <NotesList resourceType="event" resourceId={eventId} />
          </div>

          <div className="details-col">
            <ChipSection
              titleKey="events.detail.sections.topics"
              chips={event.topics ?? []}
              onAdd={(v) => addChip('topics', v)}
              onRemove={(v) => removeChip('topics', v)}
              placeholder={t('events.detail.input.topicPlaceholder')}
            />
            <ChipSection
              titleKey="events.detail.sections.partners"
              chips={event.partners ?? []}
              variant="navy"
              onAdd={(v) => addChip('partners', v)}
              onRemove={(v) => removeChip('partners', v)}
              placeholder={t('events.detail.input.partnerPlaceholder')}
            />
            <ChipSection
              titleKey="events.detail.sections.customHashtags"
              subtitleKey="events.detail.sections.customHashtagsHint"
              chips={event.customHashtags ?? []}
              variant="terra"
              onAdd={(v) => addChip('customHashtags', v)}
              onRemove={(v) => removeChip('customHashtags', v)}
              placeholder={t('events.detail.input.hashtagPlaceholder')}
            />

            <SpeakerList
              eventId={eventId}
              initial={event.speakers ?? []}
              readOnly={usingMockData}
            />

            <SessionList
              eventId={eventId}
              initial={event.sessions ?? []}
              readOnly={usingMockData}
            />

            <DraftList
              eventId={eventId}
              initial={event.drafts ?? []}
              readOnly={usingMockData}
            />
          </div>
        </div>
      ) : (
        <ComingSoonPanel phase={5} tabLabel={activeTabLabel} />
      )}

      {showDelete && (
        <ConfirmDeleteDialog
          eventName={event.name}
          onCancel={() => setShowDelete(false)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

      {showCreateDraft && (
        <CreateDraftModal
          eventId={eventId}
          onClose={() => setShowCreateDraft(false)}
        />
      )}

      <SaveToast status={saveStatus} />
    </div>
  );
}
